// src/components/operationCenter/widgets/BullMarketIndicatorsWidget.tsx
// Widget wrapper for Bull Market Peak Indicators table

import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import type { WidgetProps } from "@/types/dashboard";
import { useBullMarketPeakIndicators } from "@/hooks/coinglass/useBullMarketPeakIndicators";

const BullMarketIndicatorsWidget: React.FC<WidgetProps> = () => {
  const { data, isLoading, isError, error } = useBullMarketPeakIndicators();

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 p-4">
        <TrendingDown className="w-8 h-8 mb-2 opacity-50" />
        <div className="text-sm">
          {error ? (error as Error).message : "No data available"}
        </div>
      </div>
    );
  }

  const { metadata, indicators } = data;

  // Sort indicators: AHR999 first, then Puell, then Pi Cycle, then others
  const sortedIndicators = [...indicators].sort((a, b) => {
    if (a.indicatorName === "Bitcoin Ahr999 Index") return -1;
    if (b.indicatorName === "Bitcoin Ahr999 Index") return 1;
    if (a.indicatorName.includes("Puell Multiple")) return -1;
    if (b.indicatorName.includes("Puell Multiple")) return 1;
    if (a.indicatorName.includes("Pi Cycle Top")) return -1;
    if (b.indicatorName.includes("Pi Cycle Top")) return 1;
    return 0;
  });

  const getChangeIcon = (changeValue: number) => {
    if (changeValue > 0) return "↑";
    if (changeValue < 0) return "↓";
    return "→";
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header with summary */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50 bg-slate-800/30 flex-shrink-0">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-xs uppercase tracking-wide text-slate-300">
            Bull Market Indicators
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-slate-400">
            Hit:{" "}
            <span className="text-white font-medium">
              {metadata.indicatorsHit}/{metadata.totalIndicators}
            </span>
          </span>
          <span className="text-emerald-400 font-medium">
            {metadata.averageProgress}%
          </span>
        </div>
      </div>

      {/* Progress gauge */}
      <div className="px-3 py-2 border-b border-slate-700/30 flex-shrink-0">
        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
          <span>Hold 100%</span>
          <span>0% Sell</span>
        </div>
        <div className="h-1.5 rounded-full relative bg-gradient-to-r from-emerald-500 via-yellow-500 to-red-500">
          <div
            className="absolute top-[-2px] w-0.5 h-2.5 bg-white shadow-md rounded-full"
            style={{ left: `${metadata.averageProgress}%` }}
          />
        </div>
      </div>

      {/* Indicators list */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
        <table className="w-full text-[11px] table-fixed">
          <thead className="sticky top-0 bg-slate-900/90 backdrop-blur-sm">
            <tr className="text-slate-400 border-b border-slate-700/50">
              <th className="px-2 py-1.5 text-left font-medium w-[45%]">Indicator</th>
              <th className="px-2 py-1.5 text-right font-medium w-[22%]">Current</th>
              <th className="px-2 py-1.5 text-center font-medium w-[10%]">Hit</th>
              <th className="px-2 py-1.5 text-right font-medium w-[23%]">Progress</th>
            </tr>
          </thead>
          <tbody>
            {sortedIndicators.map((indicator, index) => (
              <tr
                key={index}
                className="border-b border-slate-800/50 hover:bg-slate-800/30"
              >
                <td className="px-2 py-1.5 text-slate-200 truncate">
                  {indicator.indicatorName}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  <span
                    className={
                      indicator.changeValue >= 0
                        ? "text-emerald-400"
                        : "text-red-400"
                    }
                  >
                    {indicator.currentValue.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}{" "}
                    {getChangeIcon(indicator.changeValue)}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-center">
                  <span
                    className={
                      indicator.hitStatus ? "text-emerald-400" : "text-red-400"
                    }
                  >
                    {indicator.hitStatus ? "✓" : "✗"}
                  </span>
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center justify-end gap-1">
                    <div className="flex-1 max-w-[50px] h-1 bg-slate-700/50 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-yellow-500"
                        style={{
                          width: `${Math.min(indicator.progress, 100)}%`,
                        }}
                      />
                    </div>
                    <span className="text-slate-400 tabular-nums">
                      {indicator.progress}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BullMarketIndicatorsWidget;
